import { curry } from 'rich-text-lib/utils/iterables';
import { getAnchorPluginSetting } from '../utils';
import { HS_ANCHOR_ATTRIBUTE, TEMP_ANCHOR_ATTRIBUTE } from '../constants/hsanchor';
import { hsanchorConfig, AnchorBlockSelectors } from '../constants/hsanchor';
const filterAnchors = curry((search, exact, anchor) => {
  if (!anchor.id) return false;
  return exact ? anchor.id === search : anchor.id.toLowerCase().indexOf(search.toLowerCase()) >= 0;
});
export const isNaiveAnchorNode = node => {
  if (!node) return false;
  return node.nodeName === 'A' && !node.hasAttribute('href') && !node.hasAttribute('onclick') && (node.id || node.name || node.hasAttribute(HS_ANCHOR_ATTRIBUTE));
};
export const isAnchorNode = node => {
  if (!node || node.nodeType !== Node.ELEMENT_NODE || node.hasAttribute('data-insert') // Temporary link inserts have [data-insert] attr
  ) {
    return false;
  }
  return node.nodeName === 'A' && (node.hasAttribute(TEMP_ANCHOR_ATTRIBUTE) || node.hasAttribute(HS_ANCHOR_ATTRIBUTE));
};

// Synchronous Fetch:
// Fetch all the anchors on the page, or the one(s) that match the {anchorId}. This will
// potentially grab anchors from other rich text modules as long as they have the [data-hs-anchor]
export const fetchAnchorsOnPage = (editor, search = '', exact = false) => {
  const doc = editor.getDoc();
  const anchorEls = [...doc.querySelectorAll(`a[${HS_ANCHOR_ATTRIBUTE}]`)].filter(filterAnchors(search, exact));
  return anchorEls;
};
export const scrollAnchorIntoViewIfNeeded = (editor, anchorNode) => {
  const anchorRect = anchorNode.getBoundingClientRect();
  if (editor.settings.inline) {
    // Scroll to anchor if above the toolbar or below the window bottom
    const toolbar = document.querySelector(editor.settings.fixed_toolbar_container || '');
    const toolbarRect = toolbar && toolbar.getBoundingClientRect();
    if (toolbarRect && anchorRect.y < toolbarRect.y) {
      anchorNode.scrollIntoView();
    } else if (anchorRect.y > window.innerHeight) {
      anchorNode.scrollIntoView();
    }
  } else {
    // Scroll to anchor if outside the iframe boundaries since the bounding
    // rect is relative to the iframe's
    const iframeRect = editor.iframeElement.getBoundingClientRect();
    if (anchorRect.y <= 0 || anchorRect.y > iframeRect.height) {
      anchorNode.scrollIntoView();
    }
  }
};

// Async Fetch:
// In the anchor plugin getAllContentAnchors will be treated as an async function
// For richText in the sidebar it has to be async because we are relying on
// window.postMessage to gather the anchor information
export const getFetchAnchors = editor => {
  const getAllContentAnchors = getAnchorPluginSetting(editor, hsanchorConfig.GET_ALL_CONTENT_ANCHORS);
  return (search = '', exact = false) => getAllContentAnchors ? getAllContentAnchors().then(anchors => anchors.filter(filterAnchors(search, exact))) : Promise.resolve(fetchAnchorsOnPage(editor, search, exact));
};

// Find outermost AnchorBlockSelector for an element to extract out to
const findAnchorBlockParent = (parent, outerTag) => {
  if (!parent) return outerTag;
  if (parent.matches(AnchorBlockSelectors.tier1) || parent.matches(AnchorBlockSelectors.tier2)) {
    return findAnchorBlockParent(parent.parentElement, parent);
  }
  return findAnchorBlockParent(parent.parentElement, outerTag);
};

// Find the parent node by tag of an element if it exists
const getParentElement = (parent, tags) => {
  if (!parent) return null;
  if (tags.includes(parent.nodeName)) {
    return parent;
  }
  return getParentElement(parent.parentElement, tags);
};

// Check if the parent element is a td or li element
export const isListTable = parentBlock => {
  if (!parentBlock) return null;
  const listTableNode = getParentElement(parentBlock, ['LI', 'TD']);
  const parentTable = getParentElement(parentBlock, ['TABLE']);
  const parentList = getParentElement(parentBlock, ['UL', 'OL']);

  // Check if within a table or list if element is not an li / td
  if (!listTableNode) {
    if (parentTable) return parentTable;
    if (parentList) return parentList;
    return null;
  }

  // Check if li / td is within a pre or blockquote element
  const preBlockParent = findAnchorBlockParent(listTableNode, null);
  if (preBlockParent) return preBlockParent;

  // If first list item set anchor to be overarching ul / ol
  if (parentList && listTableNode === parentList.querySelector('li')) {
    return parentList;
  }

  // Check if anchor is already in node. If so return first child after anchor else return firstChild
  if (listTableNode.firstElementChild && listTableNode.firstElementChild.tagName && listTableNode.firstElementChild.getAttribute('data-hs-anchor')) {
    return listTableNode.childNodes[1];
  }
  return listTableNode.firstChild;
};